import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { Store, Plus, X, Trash2, PenLine, Star, StarOff, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminMall() {
  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", category: "Wellness Products", website: "", email: "", phone: "", address: "", is_featured: false });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bizRes, statsRes] = await Promise.all([
        apiClient.get('/admin/mall/businesses'),
        apiClient.get('/admin/mall/stats')
      ]);
      setBusinesses(bizRes.data.businesses);
      setTotal(bizRes.data.total);
      setCategories(bizRes.data.categories || []);
      setStats(statsRes.data);
    } catch (err) { console.error('Failed to fetch mall data:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ name: "", description: "", category: "Wellness Products", website: "", email: "", phone: "", address: "", is_featured: false });
    setEditId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await apiClient.put(`/admin/mall/businesses/${editId}`, form);
      } else {
        await apiClient.post('/admin/mall/businesses', form);
      }
      resetForm();
      fetchData();
    } catch (err) { console.error('Failed to save business:', err); }
    finally { setSaving(false); }
  };

  const startEdit = (b) => {
    setForm({ name: b.name, description: b.description, category: b.category, website: b.website || "", email: b.email || "", phone: b.phone || "", address: b.address || "", is_featured: b.is_featured });
    setEditId(b.business_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this business?")) return;
    try { await apiClient.delete(`/admin/mall/businesses/${id}`); fetchData(); }
    catch (err) { console.error('Failed to delete business:', err); }
  };

  const toggleFeatured = async (b) => {
    try {
      await apiClient.put(`/admin/mall/businesses/${b.business_id}`, { is_featured: !b.is_featured });
      fetchData();
    } catch (err) { console.error('Failed to toggle featured:', err); }
  };

  const statCards = stats ? [
    { label: "Total Listings", value: stats.total, color: "bg-blue-500/10 text-blue-400" },
    { label: "Active", value: stats.active, color: "bg-emerald-500/10 text-emerald-400" },
    { label: "Featured", value: stats.featured, color: "bg-amber-500/10 text-amber-400" },
  ] : [];

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-mall">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white mb-1">ME.Wellness Mall</h1>
            <p className="text-white/40 text-sm">Virtual Business Directory — {total} listings</p>
          </div>
          <Button onClick={() => showForm ? resetForm() : setShowForm(true)} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl gap-2" data-testid="new-business-btn">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Cancel" : "Add Business"}
          </Button>
        </div>

        {!loading && stats && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            {statCards.map(s => (
              <div key={s.label} className="bg-[#161821] border border-white/5 rounded-2xl p-4">
                <p className="text-2xl font-semibold text-white">{s.value}</p>
                <p className="text-white/40 text-xs">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-[#161821] border border-white/5 rounded-2xl p-6 mb-6 space-y-4" data-testid="business-form">
            <div className="grid md:grid-cols-2 gap-4">
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Business name" className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" required data-testid="biz-name" />
              <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="bg-[#0f1117] border border-white/10 text-white rounded-xl px-3 py-2" data-testid="biz-category">
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Business description..." className="w-full bg-[#0f1117] border border-white/10 text-white placeholder:text-white/30 rounded-xl p-3 min-h-[100px] resize-none" required data-testid="biz-desc" />
            <div className="grid md:grid-cols-2 gap-4">
              <Input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} placeholder="Website URL" className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" data-testid="biz-website" />
              <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Contact email" className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" data-testid="biz-email" />
              <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="Phone number" className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" data-testid="biz-phone" />
              <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="Address" className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" data-testid="biz-address" />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({...form, is_featured: e.target.checked})} className="rounded" data-testid="biz-featured" />
                <span className="text-amber-400 text-sm">Featured Listing</span>
              </label>
              <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl ml-auto" data-testid="biz-submit">
                {saving ? "Saving..." : (editId ? "Update" : "Add Business")}
              </Button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : businesses.length > 0 ? (
          <div className="space-y-3">
            {businesses.map(b => (
              <div key={b.business_id} className={`bg-[#161821] border rounded-2xl p-5 ${b.is_featured ? "border-amber-500/30" : "border-white/5"}`} data-testid={`biz-${b.business_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {b.is_featured && <Star className="w-4 h-4 text-amber-400 fill-amber-400" />}
                      <span className="px-2 py-0.5 rounded-full text-xs bg-white/5 text-white/50">{b.category}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${b.status === "active" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>{b.status}</span>
                    </div>
                    <h3 className="text-white font-medium mb-1">{b.name}</h3>
                    <p className="text-white/50 text-sm mb-2">{b.description}</p>
                    <div className="flex gap-4 text-white/30 text-xs">
                      {b.website && <span>{b.website}</span>}
                      {b.email && <span>{b.email}</span>}
                      {b.phone && <span>{b.phone}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggleFeatured(b)} className={`p-2 rounded-xl transition-colors ${b.is_featured ? "text-amber-400 hover:bg-amber-500/10" : "text-white/20 hover:bg-white/5"}`} title={b.is_featured ? "Remove featured" : "Make featured"}>
                      {b.is_featured ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                    </button>
                    <button onClick={() => startEdit(b)} className="p-2 rounded-xl hover:bg-blue-500/10 text-white/30 hover:text-blue-400"><PenLine className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(b.business_id)} className="p-2 rounded-xl hover:bg-red-500/10 text-white/20 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#161821] border border-white/5 rounded-2xl p-12 text-center">
            <Store className="w-12 h-12 text-white/10 mx-auto mb-4" /><p className="text-white/30">No businesses listed yet</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
